import { Switch, Route, useLocation, Redirect } from "wouter";
import { useHashLocation } from "wouter/use-hash-location";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/not-found";
import Landing from "@/pages/Landing";
import Home from "@/pages/Home";
import PropertyDetail from "@/pages/PropertyDetail";
import Commercial from "@/pages/Commercial";
import Login from "./pages/Auth/Login";
import { store } from '@/Redux/store';
import { Provider, useSelector } from 'react-redux';
import Register from "./pages/Auth/Register";
import Account from "./pages/Account";
import { Router as WouterRouter } from "wouter";
import Listing from "./pages/Buyer/Listing";
import Favorites from "./pages/Buyer/Favorites";
import CustomLoaderProvider from "./components/Loader";
import Inquires from "./pages/Seller/Inquires";
import Chat from "./pages/Seller/Chat";
import { NotificationProvider, useNotify } from "./notifications/NotificationProvider";
import { useEffect } from "react";
import { initSocket } from "./socket/client";
import VerifyEmail from "./pages/VerifyEmail";
import ResetPassword from "./pages/Auth/ResetPassword";
import AdminDashboard from "./pages/Admin/AdminDashboard";
import AdminProperty from "./pages/Admin/AdminProperty";
import AdminUsers from "./pages/Admin/AdminUsers";
import SellerPlans from "./pages/SellerPlans";
import SellerPayments from "./pages/SellerPayments";
import AdminSubscriptions from "./pages/Admin/AdminSubscriptions";
import Dashboard from "./pages/Dashboard";
import DashboardLayout from "./layouts/DashboardLayout";
import Task from "@/pages/Tasks/Index";
import TaskAddEdit from "@/pages/Tasks/AddEdit";
import Vendors from "@/pages/Vendors/Index";
import VendorAddEdit from "./pages/Vendors/AddEdit";
import { ThemeProvider } from "@mui/material";
import theme from "./theme/theme";
import AddProperty from "./pages/Property/Add";
import Property from "./pages/Property/Index";
import TaskCalendar from "./pages/Tasks/Calendar";
import Feedback from "./pages/Feedback/Index";
import ResidentList from "./pages/Resident/Index";
import ResidentAdd from "./pages/Resident/Add";
import Message from "./pages/Message/Index";
import MeetingList from "./pages/Meetings/Index";

function Router() {
  const { isAuthenticated, token, user } = useSelector((state: any) => state?.auth);
  const { notify } = useNotify();

  useEffect(() => {
    const sock = initSocket(token);

    sock.once('connect', () => {
      console.log('✅ Socket connected:', sock?.id);
    });

    sock.on('message:global', (msg: any) => {
      if (msg?.recieverId == user?.id) {
        notify({ title: msg.name, message: msg.text })
      }
    });

    return () => {
      sock.off('message:global');
    };
  }, [token]);



  return (
    <Switch>
      <Route path="/" component={() => (
        <Redirect to={isAuthenticated ? "/dashboard" : "/login"} />
      )} />
      <Route path="/login" component={Login} />
      <Route path="/register" component={Register} />
      <Route path="/verify-email/:token" component={VerifyEmail} />
      <Route path="/reset-password/:token" component={ResetPassword} />
      <DashboardLayout>
        <Route path="/dashboard" component={Dashboard} />
        <Route path="/resident" component={ResidentList} />
        <Route path="/resident/add" component={ResidentAdd} />
        <Route path="/resident/edit/:id" component={ResidentAdd} />
        <Route path="/property" component={Property} />
        <Route path="/property/add" component={AddProperty} />
        <Route path="/property/edit/:id" component={AddProperty} />
        <Route path="/vendors" component={Vendors} />
        <Route path="/vendors/add" component={VendorAddEdit} />
        <Route path="/vendors/edit/:id" component={VendorAddEdit} />
        <Route path="/task" component={Task} />
        <Route path="/task/add" component={TaskAddEdit} />
        <Route path="/task/edit/:id" component={TaskAddEdit} />
        <Route path="/task/calendar" component={TaskCalendar} />
        <Route path="/feedback" component={Feedback} />
        <Route path="/message" component={Message} />
        <Route path="/meeting" component={MeetingList} />
      </DashboardLayout>
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Provider store={store}>
        <NotificationProvider>
          <CustomLoaderProvider>
            <TooltipProvider>
              <Toaster />
              <RouterHooked />
            </TooltipProvider>
          </CustomLoaderProvider>
        </NotificationProvider>
      </Provider>
    </QueryClientProvider>
  );
}

// ✅ Hook Wouter to use hash router
function RouterHooked() {
  return (
    <RouterContext>
      <ThemeProvider theme={theme}>
        <Router />
      </ThemeProvider>
    </RouterContext>
  );
}
function RouterContext({ children }: { children: React.ReactNode }) {
  return (
    <WouterRouter hook={useHashLocation}>
      {children}
    </WouterRouter>
  );
}

export default App;
